<template>
  <v-container
    ref="parentDiv"
    v-resize="onResize"
    fluid
    style="overflow: hidden !important; width: 100%;"
    class="fill-height pa-0 ma-0"
  >
    <v-data-table
      dense
      :headers="headers"
      :items="items"
      fixed-header
      :expanded.sync="expanded"
      v-model="selectedTimeSeries"
      :height="tableContainerCalculatedHeight"
      style="width: 100%; height: 100%;"
      :page.sync="page"
      tabindex="-1"
      :item-class="itemRowBackground"
      :server-items-length="count"
      :footer-props="tableFooterProps"
      :options.sync="tableOptions"
      @click:row="showInfo"
      :loading="loading"
      item-key="OBJECTID"
      show-select
      class="elevation-1 pa-0 ma-0"
    >
      <template
        v-slot:[`item.data-table-select`]="{ isSelected, select, item }"
      >
        <checkbox-custom
          @click.stop
          :value="isSelected"
          @input="select($event)"
          :showLabel="false"
          label="Velg rad"
          @enter="showInfo(item)"
          @focus="onRowFocus(item)"
          @blur="onRowBlur(item)"
        />
      </template>

      <template v-slot:[`header.data-table-select`]="{ on, props }">
        <checkbox-custom
          v-bind="props"
          v-on="on"
          :showLabel="false"
          label="Velg alle rader på denne siden"
          :indeterminate="props.indeterminate"
        />
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import MapService from "../../services/MapService";
import CheckboxCustom from "../common/CheckboxCustom.vue";

export default {
  name: "DataTable",
  components: {
    CheckboxCustom,
  },
  data: () => {
    return {
      mapService: null,
      mapExtent: {},
      loading: false,
      useBuffer: false,
      height: 0,
      buffer: Object,
      countQueryRunning: false,
      count: 0,
      columns: [],
      selectedTimeSeries: [],
      controller: new AbortController(),
      signal: null,
      defaultMapExtent: null,
      focusedRowID: null,
      expanded: [],
      dateRange: {
        start: new Date(new Date().setMonth(new Date().getMonth() - 2)),
        end: new Date(),
      },
      items: [],
      page: 1,
      tableOptions: {
        itemsPerPage: 100,
        sortBy: [],
        sortDesc: [false],
      },
      tableFooterProps: {
        "items-per-page-options": [100, 200, 500],
      },
      tab: null,
    };
  },
  props: {
    tableHeight: {
      type: String,
      default: "calc(100vh - 228px)",
    },
    isSplitView: {
      type: Boolean,
      default: false,
    },
    clearSelectedTimeSeriesOnStartup: {
      type: Boolean,
      default: true,
    },
    dataUrl: {
      type: String,
    },
    headers: {
      type: Array,
    },
    allHeaders: {
      type: Array,
    },
    queryExpression: {
      type: String,
    },
    sortBy: {
      type: String,
    },
    selectTimeSeries: {
      type: Array,
    },
    showInfoOnClick: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapState([
      "splitSize",
      "chartDateRange",
      "tabell",
      "activeGeographicFilterMode",
      "useMapExtent",
      "currentMapExtent",
      "bufferSettings",
    ]),
    tableContainerCalculatedHeight: function() {
      if (!this.isSplitView) {
        return this.tableHeight;
      }
      if (this.splitSize.length > 0) {
        return this.splitSize[1] * (window.innerHeight / 100) - 170 + "px";
      } else {
        return 50 * (window.innerHeight / 100) - 180 + "px";
      }
    },
  },
  watch: {
    selectedTimeSeries: function(newValue) {
      this.$emit("selection-changed", newValue);
    },
    queryExpression: function(newQueryExpression) {
      this.updateTable(newQueryExpression);
    },
    tableOptions: {
      handler() {
        this.loading = true;
        this.mapService
          .getData(
            this.dataUrl,
            this.queryExpression,
            this.currentMapExtent,
            this.useBuffer,
            this.buffer,
            this.useMapExtent,
            this.defaultMapExtent,
            this.tableOptions,
            this.tableOptions.page
          )
          .then((resp) => {
            this.$eventHub.$emit("query-changed", resp.query, resp.queryTask);
            this.count = resp.count;
            this.updateTableData(resp.data);
          });
      },
      deep: true,
    },
    chartDateRange: function(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.dateRange = newVal;
      }
    },
    useMapExtent: function(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.toggleUseMapExtent();
      }
    },
    currentMapExtent: function(newExtent, oldExtent) {
      if (
        newExtent !== oldExtent &&
        this.activeGeographicFilterMode !== "Ingen kartavgrensning"
      ) {
        this.updateTable(this.queryExpression);
      }
    },
    activeGeographicFilterMode: function(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.updateTable(this.queryExpression);
      }
    },
    count: function(newCount, oldCount) {
      if (newCount !== oldCount) {
        if (this.clearSelectedTimeSeriesOnStartup) {
          this.selectedTimeSeries = [];
        }
        this.$emit("count-changed", newCount);
      }
    },
  },
  methods: {
    ...mapActions([
      "setTimeSeriesCount",
      "setShowInfoDrawer",
      "setClickedTimeSeries",
    ]),
    onResize: function() {
      this.$forceCompute("tableContainerCalculatedHeight");
    },
    onRowFocus(item) {
      this.focusedRowID = item.OBJECTID;
    },
    onRowBlur() {
      this.focusedRowID = null;
    },
    itemRowBackground(item) {
      if (item.OBJECTID === this.focusedRowID) {
        return "table-row-focus-style";
      } else {
        return "";
      }
    },
    showInfo(item) {
      if (this.showInfoOnClick) {
        this.setShowInfoDrawer(true);
        this.setClickedTimeSeries([{ attributes: item }]);
      }
    },
    updateTable: function(queryExpression) {
      this.loading = true;
      this.page = 1;
      this.mapService
        .getCount(
          this.dataUrl,
          queryExpression,
          this.currentMapExtent,
          this.useBuffer,
          this.buffer,
          this.useMapExtent
        )
        .then((result) => {
          this.countQueryRunning = false;
          this.count = result;
          this.$eventHub.$emit("timeseries-count-change", this.count);

          this.mapService
            .getData(
              this.dataUrl,
              this.queryExpression,
              this.currentMapExtent,
              this.useBuffer,
              this.buffer,
              this.useMapExtent,
              this.defaultMapExtent,
              this.tableOptions,
              this.page
            )
            .then((resp) => {
              this.$eventHub.$emit("query-changed", resp.query, resp.queryTask);
              this.count = resp.count;
              this.updateTableData(resp.data);
            });
        });
    },
    updateTableData: function(resp) {
      let features = resp.features;
      let tidsserierArray = [];
      for (let i = 0; i < features.length; i++) {
        let formattedTimeSeries = this.getFormattedTimeSeries(features[i]);
        tidsserierArray.push(formattedTimeSeries);
      }
      this.items = tidsserierArray;
      this.loading = false;
      return { data: tidsserierArray, count: this.count };
    },
    getFormattedTimeSeries: function(feature) {
      let featureAttributes = feature.attributes;
      let formattedTimeSeries = featureAttributes;

      formattedTimeSeries.status =
        featureAttributes.status === "N" ? "Nedlagt" : "I drift";

      formattedTimeSeries.Date_Slutt = featureAttributes.Date_Slutt
        ? new Date(featureAttributes.Date_Slutt).toLocaleDateString("nb-NO")
        : "-";

      formattedTimeSeries.Date_Start = new Date(
        featureAttributes.Date_Start
      ).toLocaleDateString("nb-NO");

      formattedTimeSeries.dataFraDato = new Date(
        featureAttributes.dataFraDato
      ).toLocaleDateString("nb-NO");

      formattedTimeSeries.dt_min_start_time = featureAttributes.dt_min_start_time
        ? new Date(featureAttributes.dt_min_start_time).toLocaleDateString(
            "nb-NO"
          )
        : "-";

      formattedTimeSeries.dt_max_end_time = featureAttributes.dt_max_end_time
        ? new Date(featureAttributes.dt_max_end_time).toLocaleDateString(
            "nb-NO"
          )
        : "-";

      formattedTimeSeries.dataTilDato = featureAttributes.dataTilDato
        ? new Date(featureAttributes.dataTilDato).toLocaleDateString("nb-NO")
        : "-";

      formattedTimeSeries.findataFraDato = featureAttributes.findataFraDato
        ? new Date(featureAttributes.findataFraDato).toLocaleDateString("nb-NO")
        : "-";
      for (let key in formattedTimeSeries) {
        let val = formattedTimeSeries[key];
        if (val === 0 || (val && val !== "Null")) {
          formattedTimeSeries[key] = this.getFormattedValue(key, val);
        } else {
          formattedTimeSeries[key] = "Ikke angitt";
        }
      }
      formattedTimeSeries.geometry = feature.geometry;
      return formattedTimeSeries;
    },
    oppdaterBufferInnstillinger: function(useBuffer, buffer) {
      this.useBuffer = useBuffer;
      this.buffer = buffer;
      this.updateTable(this.queryExpression);
    },
    getFormattedValue: function(key, rawValue) {
      let attributes = this.allHeaders.filter((item) => item.value === key);

      if (attributes.length !== 0) {
        let attribute = attributes[0];
        if (Object.prototype.hasOwnProperty.call(attribute, "valueMapping")) {
          let values = attribute.valueMapping.filter((value) => {
            return (
              value.valueFrom ===
              (this.isInt(rawValue) ? "" + rawValue + "" : rawValue)
            );
          });
          if (values.length > 0) {
            return values[0].valueTo;
          }
        }

        if (
          (this.isInt(rawValue) || this.isFloat(rawValue)) &&
          Math.round(rawValue) !== rawValue
        ) {
          return (Math.round(rawValue * 100) / 100).toFixed(2);
        } else {
          return rawValue;
        }
      } else {
        return rawValue;
      }
    },
    isInt: function(n) {
      return Number(n) === n && n % 1 === 0;
    },
    isFloat: function(n) {
      return Number(n) === n && n % 1 !== 0;
    },
    toggleUseMapExtent() {
      this.updateTable(this.queryExpression);
    },
  },
  created: function() {
    this.$eventHub.$on("buffer:oppdatert", this.oppdaterBufferInnstillinger);
  },
  mounted: function() {
    this.selectedTimeSeries = JSON.parse(JSON.stringify(this.selectTimeSeries));
    this.tableOptions.sortBy.push(this.sortBy);
    this.mapService = new MapService();
    this.defaultMapExtent = JSON.parse(JSON.stringify(this.currentMapExtent));
    this.buffer = this.bufferSettings;
    this.useBuffer = this.bufferSettings !== null;
    this.updateTable(this.queryExpression);
  },
};
</script>

<style>
.split {
  overflow: hidden;
}

.theme--light.v-datatable .v-datatable__actions {
  position: fixed;
  bottom: 0;
  width: 100%;
}

.v-data-table-header th {
  white-space: nowrap;
}

.table-row-focus-style {
  background-color: #fd0 !important;
}
</style>
